import * as React from "react";
import { Command } from "cmdk";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Building2,
  FolderKanban,
  Search,
  Sparkles,
  UserRound,
  Users,
} from "lucide-react";
import { useCommandPalette } from "@/context/CommandPaletteContext";
import { useCopilot } from "@/context/CopilotContext";
import { NAV_ITEMS } from "@/app/nav";
import { CLIENTS, DEPARTMENTS, EMPLOYEES, ALERTS, PROJECTS } from "@/data/mock";
import { formatCurrency } from "@/lib/formatters";
import { cn } from "@/lib/cn";

const MAX_RESULTS_PER_GROUP = 5;

export function CommandPalette() {
  const { open, setOpen } = useCommandPalette();
  const { ask } = useCopilot();
  const navigate = useNavigate();
  const [query, setQuery] = React.useState("");

  React.useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  function go(path: string) {
    navigate(path);
    setOpen(false);
  }

  function askCopilot(prompt: string) {
    ask(prompt);
    setOpen(false);
  }

  const q = query.trim().toLowerCase();

  const matchedClients = q ? CLIENTS.filter((c) => c.name.toLowerCase().includes(q)).slice(0, MAX_RESULTS_PER_GROUP) : [];
  const matchedEmployees = q ? EMPLOYEES.filter((e) => e.name.toLowerCase().includes(q)).slice(0, MAX_RESULTS_PER_GROUP) : [];
  const matchedDepartments = q ? DEPARTMENTS.filter((d) => d.name.toLowerCase().includes(q)).slice(0, MAX_RESULTS_PER_GROUP) : [];
  const matchedProjects = q ? PROJECTS.filter((p) => p.name.toLowerCase().includes(q)).slice(0, MAX_RESULTS_PER_GROUP) : [];
  const matchedAlerts = q ? ALERTS.filter((a) => a.issue.toLowerCase().includes(q) || a.scope.toLowerCase().includes(q)).slice(0, MAX_RESULTS_PER_GROUP) : [];
  const matchedNav = NAV_ITEMS.filter((n) => !q || n.label.toLowerCase().includes(q));

  const hasSearchResults =
    matchedClients.length + matchedEmployees.length + matchedDepartments.length + matchedProjects.length + matchedAlerts.length > 0;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-[2px] overlay-anim" />
        <DialogPrimitive.Content
          className="dialog-anim fixed left-1/2 top-[12%] z-50 w-full max-w-xl -translate-x-1/2 overflow-hidden rounded-xl border border-border bg-card shadow-2xl"
          aria-describedby={undefined}
        >
          <DialogPrimitive.Title className="sr-only">Command palette</DialogPrimitive.Title>
          <Command shouldFilter={false} loop className="flex flex-col">
            <div className="flex items-center gap-2 border-b border-border px-4">
              <Search className="size-4 shrink-0 text-muted-foreground" />
              <Command.Input
                autoFocus
                value={query}
                onValueChange={setQuery}
                placeholder="Search clients, people, departments, alerts, projects… or jump to a page"
                className="h-12 w-full bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
              />
              <kbd className="hidden shrink-0 rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground sm:block">
                esc
              </kbd>
            </div>

            <Command.List className="max-h-[60vh] overflow-y-auto p-2">
              <Command.Empty className="py-10 text-center text-sm text-muted-foreground">
                No results for &ldquo;{query}&rdquo;.
              </Command.Empty>

              {matchedNav.length > 0 && (
                <Command.Group heading="Navigate" className="px-2 pb-2 pt-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground [&_[cmdk-group-heading]]:mb-1.5">
                  {matchedNav.map((item) => (
                    <Command.Item
                      key={item.path}
                      value={`nav-${item.path}`}
                      onSelect={() => go(item.path)}
                      className={cn(
                        "flex cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-2 text-sm text-foreground",
                        "data-[selected=true]:bg-accent",
                      )}
                    >
                      <item.icon className="size-4 text-muted-foreground" />
                      {item.label}
                    </Command.Item>
                  ))}
                </Command.Group>
              )}

              {!q && (
                <Command.Group heading="Ask AI Copilot" className="px-2 pb-2 pt-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground [&_[cmdk-group-heading]]:mb-1.5">
                  {["What needs my attention today?", "Which clients are at risk?", "Where are we behind target?"].map((prompt) => (
                    <Command.Item
                      key={prompt}
                      value={`ai-${prompt}`}
                      onSelect={() => askCopilot(prompt)}
                      className="flex cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-2 text-sm text-foreground data-[selected=true]:bg-accent"
                    >
                      <Sparkles className="size-4 text-brand" />
                      {prompt}
                    </Command.Item>
                  ))}
                </Command.Group>
              )}

              {q && hasSearchResults && (
                <>
                  {matchedClients.length > 0 && (
                    <Command.Group heading="Clients" className="px-2 pb-2 pt-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground [&_[cmdk-group-heading]]:mb-1.5">
                      {matchedClients.map((c) => (
                        <Command.Item
                          key={c.id}
                          value={`client-${c.id}`}
                          onSelect={() => go(`/clients/${c.id}`)}
                          className="flex cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-2 text-sm text-foreground data-[selected=true]:bg-accent"
                        >
                          <Users className="size-4 text-muted-foreground" />
                          <span className="flex-1 truncate">{c.name}</span>
                          <span className="text-xs text-muted-foreground">{formatCurrency(c.mrr, { compact: true })}/mo</span>
                        </Command.Item>
                      ))}
                    </Command.Group>
                  )}

                  {matchedEmployees.length > 0 && (
                    <Command.Group heading="People" className="px-2 pb-2 pt-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground [&_[cmdk-group-heading]]:mb-1.5">
                      {matchedEmployees.map((e) => (
                        <Command.Item
                          key={e.id}
                          value={`emp-${e.id}`}
                          onSelect={() => go("/people")}
                          className="flex cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-2 text-sm text-foreground data-[selected=true]:bg-accent"
                        >
                          <UserRound className="size-4 text-muted-foreground" />
                          <span className="flex-1 truncate">{e.name}</span>
                          <span className="text-xs text-muted-foreground">{e.role}</span>
                        </Command.Item>
                      ))}
                    </Command.Group>
                  )}

                  {matchedDepartments.length > 0 && (
                    <Command.Group heading="Departments" className="px-2 pb-2 pt-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground [&_[cmdk-group-heading]]:mb-1.5">
                      {matchedDepartments.map((d) => (
                        <Command.Item
                          key={d.id}
                          value={`dept-${d.id}`}
                          onSelect={() => go("/departments")}
                          className="flex cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-2 text-sm text-foreground data-[selected=true]:bg-accent"
                        >
                          <Building2 className="size-4 text-muted-foreground" />
                          {d.name}
                        </Command.Item>
                      ))}
                    </Command.Group>
                  )}

                  {matchedProjects.length > 0 && (
                    <Command.Group heading="Projects" className="px-2 pb-2 pt-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground [&_[cmdk-group-heading]]:mb-1.5">
                      {matchedProjects.map((p) => (
                        <Command.Item
                          key={p.id}
                          value={`proj-${p.id}`}
                          onSelect={() => go("/projects")}
                          className="flex cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-2 text-sm text-foreground data-[selected=true]:bg-accent"
                        >
                          <FolderKanban className="size-4 text-muted-foreground" />
                          {p.name}
                        </Command.Item>
                      ))}
                    </Command.Group>
                  )}

                  {matchedAlerts.length > 0 && (
                    <Command.Group heading="Alerts" className="px-2 pb-2 pt-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground [&_[cmdk-group-heading]]:mb-1.5">
                      {matchedAlerts.map((a) => (
                        <Command.Item
                          key={a.id}
                          value={`alert-${a.id}`}
                          onSelect={() => go("/alerts")}
                          className="flex cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-2 text-sm text-foreground data-[selected=true]:bg-accent"
                        >
                          <AlertTriangle className="size-4 text-muted-foreground" />
                          <span className="flex-1 truncate">{a.scope}</span>
                          <span className="truncate text-xs text-muted-foreground">{a.category}</span>
                        </Command.Item>
                      ))}
                    </Command.Group>
                  )}
                </>
              )}
            </Command.List>
          </Command>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
